<template>
  <section
    id="intro"
    class="text-center mb-12 container pt-12 pb-0 px-0 container--fluid"
  >
    <h1 class="display-2 font-weight-light mb-8">
      MES Arpeggiator VST – Advanced MIDI Pattern Generator for Your DAW
    </h1>
    <v-container>
      <h2 class="display-1 mb-8 headline">
        MES Arpeggiator – A Creative MIDI Arpeggiator VST for Windows & Mac with Skippable Steps, Euclidean Rhythms & Deep Pattern Control.
      </h2>
    </v-container>

    <v-container>
      <h2 class="display-1 mb-8 headline">
        How to use the MES Arpeggiator.
      </h2>
    </v-container>
    <div
      v-if="acceptedAnalyticsCookies && acceptedMarketingCookies && acceptedPreferencesCookies"
      class="video-wrapper"
    >
      <!-- Using the computed property with index directly in the template -->
      <iframe
        :src="videoUrl(0)"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      ></iframe>
    </div>
    <div v-else>
      <br>
      <h3>
        📺 Please accept 'Preferences', 'Analytical' and 'Marketing' cookies to view video content. 📺
      </h3>
    </div>
    <br>

    <v-container>
      <v-container class="text-center">
        <v-row justify="center">
          <v-col
            cols="12"
            md="6"
            class="pa-4"
          >
            <v-img
              :src="require('@/assets/MIDI-arpeggiator-plugin-vst-red.png')"
              contain
              class="mx-auto image-width"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
            class="pa-4"
          >
            <v-img
              :src="require('@/assets/MIDI-arpeggiator-plugin-vst-purple.png')"
              contain
              class="mx-auto image-width"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
            class="pa-4"
          >
            <v-img
              :src="require('@/assets/MIDI-arpeggiator-plugin-vst-green.png')"
              contain
              class="mx-auto image-width"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
            class="pa-4"
          >
            <v-img
              :src="require('@/assets/MIDI-arpeggiator-plugin-vst-yellow.png')"
              contain
              class="mx-auto image-width"
            />
          </v-col>
        </v-row>
      </v-container>
      <br>
      <div class="cta-buttons">
        <button
          class="cta-button download-button"
          @click="handleButtonClick('Download')"
        >
          Download Free Version Now
        </button>
      </div>
      <h2 class="display-1 mb-8 headline">
        MIDI Routing & Audio Setup
      </h2>
      <p class="description">
        <strong>Note:</strong> MES Arpeggiator is a <em>MIDI effect plugin</em>. It <strong>does not produce sound</strong> on its own.
        You must route its MIDI output to a virtual instrument or hardware synth for audio playback.
      </p>

      <h3 class="section-headline mt-10">
        How to Route MIDI in Popular DAWs
      </h3>

      <div class="ui-sections">
        <div class="ui-block">
          <h4 class="heading">
            Ableton Live
          </h4>
          <p>
            1. Create <strong>MIDI Track 1</strong> and load <em>MES Arpeggiator</em>.<br>
            2. Create <strong>MIDI Track 2</strong> and load your instrument (e.g. a synth).<br>
            3. On Track 2:<br>
            &nbsp;&nbsp;• Set <strong>MIDI From</strong> to: <em>Track 1 (MES Arp)</em><br>
            &nbsp;&nbsp;• In the drop-down below, choose <strong>"MES Arp"</strong> not <strong>"Pre FX"</strong> or <strong>"Post FX"</strong> to receive the processed MIDI.<br>
            &nbsp;&nbsp;• Set <strong>Monitor</strong> to: <strong>In</strong><br>
            4. Now, playing notes into Track 1 will drive your synth on Track 2 via MES Arpeggiator.
          </p>
        </div>

        <div class="ui-block">
          <h4 class="heading">
            FL Studio (with Patcher)
          </h4>
          <p>
            1. Open a <strong>Patcher</strong> and load MES Arpeggiator.<br>
            2. Add your instrument plugin.<br>
            3. Connect the <strong>MIDI output</strong> of MES to the instrument's <strong>MIDI input</strong>.
          </p>
        </div>

        <div class="ui-block">
          <h4 class="heading">
            Logic Pro
          </h4>
          <p>
            1. Create a Software Instrument track.<br>
            2. In the MIDI FX section, insert <em>MES Arpeggiator</em>.<br>
            3. Below that, add your synth or sampler.<br>
            🎵 Play notes – the arpeggiated MIDI triggers your instrument.
          </p>
        </div>

        <div class="ui-block">
          <h4 class="heading">
            Reaper / Other DAWs
          </h4>
          <p>
            - Place MES Arpeggiator on a separate track.<br>
            - Route its <strong>MIDI output</strong> to your instrument track.<br>
            - Alternatively, use <em>virtual MIDI cables</em> (e.g., IAC on macOS, loopMIDI on Windows) for advanced routing.
          </p>
        </div>
      </div>

      <p class="description mt-10">
        MES Arpeggiator gives you fine-grained MIDI control and creative freedom. Route it to your synths, samplers, or drum machines, and let it handle the rhythm while you focus on the sound.
      </p>
    </v-container>
  </section>
</template>

  <script>
  export default {
    name: 'Headline',
    data () {
      return {
        videos: [
          'Vvp_bCsC6Ro'
        ],
        acceptedAnalyticsCookies: false,
        acceptedFunctionalCookies: false,
        acceptedMarketingCookies: false,
        acceptedPreferencesCookies: false,
      }
    },

    computed: {
      videoUrl () {
        // Create a function that returns the URL based on an index
        return (index) => {
          return 'https://www.youtube.com/embed/' + this.videos[index]
        }
      }
    },

    created () {
      // Perform the initial check as soon as the component is created.
      this.checkCookieConsent()
    },
    mounted () {
      // Set up a repeating check every 2 seconds after the component is mounted.
      this.checkInterval = setInterval(this.checkCookieConsent, 2000)
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.text = JSON.stringify({
        '@context': 'https://schema.org/',
        '@type': 'SoftwareApplication',
        name: 'MES Arpeggiator VST Plugin',
        description: 'MES Arpeggiator is a powerful MIDI arpeggiator plugin for music producers. Compatible with most major DAWs including Ableton Live, FL Studio, Reaper, Cubase, Bitwig Studio, Studio One, Pro Tools, Cakewalk, Reason, Mixcraft, and Tracktion — any DAW that supports VST or AU plugins.',
        operatingSystem: 'Windows, macOS',
        applicationCategory: 'MusicProduction',
        offers: {
          '@type': 'Offer',
          price: '25.00',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'Make Electronic Sound'
          }
        }
      })
      document.head.appendChild(script)
    },

    methods: {
      checkCookieConsent () {
        if (window.cookiehub) {
          this.acceptedAnalyticsCookies = window.cookiehub.hasConsented('analytics')
          this.acceptedFunctionalCookies = window.cookiehub.hasConsented('functional')
          this.acceptedMarketingCookies = window.cookiehub.hasConsented('marketing')
          this.acceptedPreferencesCookies = window.cookiehub.hasConsented('preferences')
        }
      },

      handleButtonClick (buttonType) {
        if (this.isUserLoggedIn() && this.userPurchased()) {
          this.$router.push({ name: 'User Area' })
        } else if (this.isUserLoggedIn()) {
          this.$router.push({ name: 'Downloads' })
        } else {
          this.$router.push({ name: 'Sign Up' })
        }
      },
      isUserLoggedIn () {
        return this.$store.getters.isUserLoggedIn
      },
      userPurchased () {
        return this.$store.getters.purchased
      }
    }
  }
  </script>

  <style scoped>
  .headline {
    /* Presuming this is your existing style for '3 Unique User Interfaces' */
    font-size: 2.5rem; /* Example size, adjust as necessary */
    font-weight: light;
    margin-bottom: 8px;
  }

  .section-headline {
    /* New style for the individual UI sections, slightly smaller */
    font-size: 2.2rem; /* Slightly smaller than the main headline */
    font-weight: light;
    margin-bottom: 8px;
  }

  .middle-third {
      width: 45%; /* 100 / 3 to get one-third */
      margin: 0 auto; /* Center the div */
    }

    @media (max-width: 960px) {
      .middle-third {
        width: 100%; /* On smaller screens, take full width */
      }
    }

  /* If you have other content in .info-section that shouldn't be centered,
     you'll need to wrap the list in another div that will be flexed */
  .list-container {
    display: flex;
    justify-content: center; /* Centers the child horizontally */
    width: 100%; /* Takes up 100% of the info-section */
  }

  .info-section li {
    text-align: left; /* Ensuring list items are also aligned to the left if needed */
    line-height: 1.5; /* Improved readability for list items */
  }

  @media (max-width: 600px) {
    .image-width {
      width: 100%; /* Full width on smaller screens */
    }
  }

  .cta-buttons {
    display: flex;
    justify-content: center; /* Centering the buttons */
    padding: 20px;
    gap: 20px; /* Adds space between buttons */
  }

  .cta-button {
    padding: 10px 40px; /* Increased horizontal padding */
    border: none;
    border-radius: 5px;
    font-size: 18px; /* Slightly larger font size */
    cursor: pointer;
    transition: background-color 0.3s;
    width: 320px;
    min-width: 320px; /* Minimum width */
    background-color: #4CAF50; /* Green */
    color: white;
  }

  .cta-button:hover {
    opacity: 0.9;
  }

  .ui-sections {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .ui-block {
    background-color: #f0f0f0;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }

  .heading {
    font-size: 1.5em;
    color: #333;
  }

  .content-wrapper {
    max-width: 1200px; /* Max width to align with the image */
    margin: 0 auto; /* Centering the wrapper */
  }

  .image-width {
    width: 80%; /* This controls the image size relative to its container */
    display: block; /* Ensures the image behaves as a block element, centering with margin auto */
    margin: 0 auto; /* Center the image horizontally */
  }

  .description {
    width: 80%; /* Make the paragraph the same width as the image */
    margin: 0 auto; /* Center the paragraph horizontally */
    font-size: 1.2em; /* Increase the font size to make it stand out more */
    color: #333; /* Darker color for better readability */
    padding: 20px; /* Add some padding around the text */
    background-color: #f8f8f8; /* Light background color to make the paragraph pop */
    box-shadow: 0 2px 4px rgba(0,0,0,0.1); /* Subtle shadow to lift the text off the page */
  }

  /* Button styles */
  button {
    margin: 10px;
    padding: 10px 20px;
    font-size: 16px;
    border-radius: 5px;
    cursor: pointer;
  }

  .video-wrapper {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    height: 0;
    overflow: hidden;
    margin: auto; /* Centers the video wrapper */
  }
  .video-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  /* Default to half width for larger screens */
  @media (min-width: 768px) {
    .video-wrapper {
      width: 80%; /* Previously 50% */
      padding-bottom: 45%; /* Adjust if needed to maintain 16:9 ratio: 80% of 16:9 is ~45% */
    }
  }

  /* Adjust to 80% width for smaller screens (like iPhones) */
  @media (max-width: 767px) {
    .video-wrapper {
        width: 80%;
        padding-bottom: 45%; /* Adjust the padding-bottom to maintain the aspect ratio */
    }
  }

  </style>
